<template>
  <div>
    <h1>Lançamentos de ajuste para {{ month }}/{{ year }}</h1>
    <b-card no-body class="mt-2">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options="{ search, searching, loading }">
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
        </b-row>
      </div>
      <b-table
        hover
        :items="items"
        responsive
        :fields="tableColumns"
        show-empty
        empty-text="Não foram encontradas comissões para esta busca"
        :busy="loading"
        :no-local-sorting="true"
      >
        <template #cell(id)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.id }}</span>
          </div>
        </template>
        <template #cell(consultant_name)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.consultant_name }}</span>
          </div>
        </template>
        <template #cell(name)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.name }}</span>
            <!--
              A rolagem é uma linha só na fatura; aqui o PN abre o que ela carrega, item a item,
              sem precisar voltar ao mês anterior e somar à mão.
            -->
            <b-link
              v-if="data.item.has_rolling_details"
              class="d-block small"
              @click="data.toggleDetails"
            >
              <feather-icon
                :icon="data.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="14"
              />
              {{ data.detailsShowing ? "Ocultar composição" : "Ver composição" }}
            </b-link>
          </div>
        </template>
        <template #row-details="{ item }">
          <b-card class="mb-0 mx-1" no-body>
            <div class="p-1">
              <h6 class="mb-50">Composição da rolagem</h6>
              <rolling-items-details :rolling-id="item.id" own />
            </div>
          </b-card>
        </template>
        <template #cell(credit_type)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.credit_type }}</span>
          </div>
        </template>
        <template #cell(value)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.value | toCurrency
            }}</span>
          </div>
        </template>
        <template #cell(paying_company)="data">
          <div class="text-wrap">
            <b-badge
              :variant="payingCompanyVariant(data.item.paying_company_id)"
              :title="payingCompanyTitle(data.item.paying_company_id)"
            >
              {{ data.item.paying_company_name || "Sem empresa pagadora" }}
            </b-badge>
          </div>
        </template>
        <template #cell(reference_competence)="data">
          <div class="text-wrap">
            <span class="align-text-top">
              {{ data.item.reference_competence || "—" }}
            </span>
          </div>
        </template>
        <template #cell(launch_date)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.launch_date | onlyDate
            }}</span>
          </div>
        </template>
        <template #cell(billing_date)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.billing_date | onlyDate
            }}</span>
          </div>
        </template>
        <template #cell(comment)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.comment }}</span>
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de
              {{ totalItems }} itens</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import vSelect from "vue-select";
import * as types from "@/modules/financial_adjustment/store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import RollingItemsDetails from "@/modules/financial_adjustment/components/RollingItemsDetails";
import {
  HUB_ID,
  PLATFORM_ID,
  payingCompanyLabel,
} from "@/constants/paying_companies";
export default {
  components: {
    vSelect,
    RollingItemsDetails,
  },
  data() {
    return {
      loading: false,
      tableColumns: [
        { key: "id", label: "ID", sortable: false },
        { key: "consultant_name", label: "Nome", sortable: false },
        { key: "name", label: "Descrição do lançamento", sortable: false },
        { key: "credit_type", label: "Tipo", sortable: false },
        { key: "value", label: "Valor", sortable: false },
        // Esta é a ÚNICA tela de ajustes que um PN abre — a lista completa está atrás do menu
        // financeiro. O backend já devolvia as duas colunas abaixo, com um comentário no serviço
        // dizendo que "ele precisa saber de QUAL empresa vem cada ajuste, é literalmente o que
        // decide se aquele valor entra ou não na nota que ele vai emitir" — e o render descartava.
        //
        // O rótulo "Sem empresa pagadora" é o detector do lado do PN: lançamento sem
        // classificação fica visível para quem é afetado por ele.
        { key: "paying_company", label: "Empresa pagadora", sortable: false },
        { key: "reference_competence", label: "Competência de referência", sortable: false },
        { key: "launch_date", label: "Data de lançamento", sortable: false },
        { key: "billing_date", label: "Data de faturamento", sortable: false },
        { key: "comment", label: "Observações", sortable: false },
      ],
      currentPage: 1,
      itemsPerPage: 3,
    };
  },
  computed: {
    ...mapGetters({
      result: types.FINANCIAL_ADJUSTMENTS_BY_MONTH_RESULT,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    month() {
      return (this.$route.params?.month || 0).toString().padStart(2, "0");
    },
    year() {
      return this.$route.params.year || 0;
    },
    items() {
      return this.result?.data || [];
    },
    totalItems: function () {
      return this.result?.total || 0;
    },
    pageItemsFrom: function () {
      return this.result?.from || 0;
    },
    pageItemsTo: function () {
      return this.result?.to || 0;
    },
  },
  setup() {
    return { toast: useToast() };
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.search(this.currentPage);
  },
  methods: {
    // Mesmas cores da lista do Financeiro e do detalhe do ajuste. success/danger ficam de
    // fora de proposito: nesta tela eles ja significam credito e debito.
    payingCompanyVariant(id) {
      if (Number(id) === HUB_ID) return "light-info";
      if (Number(id) === PLATFORM_ID) return "light-primary";
      return "light-secondary";
    },
    // O CNPJ completo a um hover: o nome sozinho nao e conferivel contra a nota que o PN
    // vai emitir.
    payingCompanyTitle(id) {
      return id ? payingCompanyLabel(Number(id)) : "Este lancamento ainda nao foi classificado por CNPJ.";
    },
    ...mapActions({
      getAdjustmentsByMonth: types.GET_FINANCIAL_ADJUSTMENTS_BY_MONTH_RESULT,
    }),
    search(currentPage) {
      this.loading = true;
      this.currentPage = currentPage;
      this.getAdjustmentsByMonth({
        month: parseInt(this.month, 10),
        year: parseInt(this.year, 10),
        limit: this.itemsPerPage,
        currentPage: Number.isInteger(currentPage) ? currentPage : 1,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao obter os ajustes do mês. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
